import React, { ComponentPropsWithoutRef, Fragment } from 'react'

import { sortBy, trimStart, isUndefined } from 'lodash'

import { Box, Typography, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUniversity, faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons'

import api from 'common/api'

const SEARCH_URL = (term: string) => `/api/highschools/locations/search/${trimStart(term)}`

const DEFAULT_LIMIT = 10

type APIResponse = {
  MapLocations: Array<PlaceOption | HighSchoolOption>
  HighSchoolTotal: number
}

type PlaceOption = {
  DisplayName: string
  Latitude: number
  Longitude: number
  HighSchoolId?: never
  Type: 'cityState'
}
type HighSchoolOption = {
  DisplayName: string
  Latitude: number
  Longitude: number
  HighSchoolId: string
  HighSchoolCity: string
  HighSchoolState: string
  Type: 'highSchool'
}
type Option =
  | PlaceOption
  | HighSchoolOption
  | {
      DisplayName: string
      Type: 'button'
    }

type State = {
  error: boolean
  options: Option[]
  term: string
  limit: number
  HighSchoolTotal: number
  open: boolean
  preventClose: boolean
  value: string
  inputValue: string
}
type Props = {
  TextField?: ComponentPropsWithoutRef<typeof TextField>
  onChange: (
    mapParams: {
      latitude: number
      longitude: number
      selectedSchoolId?: string
    },
    isSchool: boolean,
  ) => void
}

class PlaceTypeahead extends React.Component<Props, State> {
  state: State = {
    error: false,
    options: [],
    term: '',
    limit: DEFAULT_LIMIT,
    HighSchoolTotal: 0,
    open: false,
    preventClose: false,
    value: '',
    inputValue: '',
  }
  getPlaces(term: string, newLimit = this.state.limit) {
    api
      .get<APIResponse>(SEARCH_URL(term) + `?hsLimit=${newLimit}&cityLimit=3`)
      .then((response) => {
        const options = sortBy(response.MapLocations, 'Type')
        this.setState({
          options,
          HighSchoolTotal: response.HighSchoolTotal,
          preventClose: false,
        })
      })
      .catch(() => {
        this.setState({
          error: true,
          preventClose: false,
        })
      })
  }
  handlePagination() {
    const newLimit = this.state.limit + 10
    this.setState({ limit: newLimit })
    this.getPlaces(this.state.term, newLimit)
  }
  renderHighSchoolRecord(item: HighSchoolOption) {
    return (
      <Fragment>
        <Box color="primary.main">
          <FontAwesomeIcon
            color="inherit"
            size="xl"
            icon={faUniversity}
          />
        </Box>
        <Box>
          <Typography>{item.DisplayName}</Typography>
          {item.HighSchoolCity && item.HighSchoolState && (
            <Typography
              variant="body2"
              color="textSecondary"
            >
              {[item.HighSchoolCity, item.HighSchoolState].join(', ')}
            </Typography>
          )}
        </Box>
      </Fragment>
    )
  }
  renderCityStateRecord(item: PlaceOption) {
    return (
      <Fragment>
        <Box color="primary.main">
          <FontAwesomeIcon
            size="xl"
            icon={faMapMarkerAlt}
          />
        </Box>
        <Typography>{item.DisplayName}</Typography>
      </Fragment>
    )
  }
  getOption(item: Option) {
    return (
      <Box
        display="flex"
        gridGap={16}
        alignItems="center"
      >
        {item.Type === 'button' ? (
          <Typography>{item.DisplayName}</Typography>
        ) : item.Type === 'highSchool' ? (
          this.renderHighSchoolRecord(item)
        ) : (
          this.renderCityStateRecord(item)
        )}
      </Box>
    )
  }
  handleSelect(selection: Option) {
    if (selection.Type === 'button') {
      return
    }
    const mapParams = {
      latitude: selection.Latitude,
      longitude: selection.Longitude,
      selectedSchoolId: selection.HighSchoolId,
      zoom: 13,
    }
    const isSchool = !isUndefined(selection.HighSchoolId)
    this.props.onChange(mapParams, isSchool)
  }
  render() {
    return (
      <Autocomplete
        value={this.state.value}
        inputValue={this.state.inputValue}
        id="place-typeahead"
        options={this.state.options}
        filterOptions={(options) => {
          if (this.state.HighSchoolTotal > 10) {
            const seeMore: Option = { Type: 'button', DisplayName: 'See More' }
            return [...options, seeMore]
          } else {
            return options
          }
        }}
        open={this.state.open}
        onChange={(_, selection, reason) => {
          if (selection === null) {
            return
          }
          if (reason === 'select-option' && typeof selection !== 'string') {
            if (selection.Type === 'button') {
              this.setState({ preventClose: true, inputValue: this.state.inputValue })
              this.handlePagination()
            } else {
              this.handleSelect(selection)
              this.setState({ open: false, inputValue: selection.DisplayName })
            }
          }
        }}
        disableCloseOnSelect
        onOpen={() => {
          if (this.state.term.length > 1 && this.state.options.length > 0) {
            this.setState({ open: true })
          }
        }}
        noOptionsText="No matches found."
        onClose={(_, reason) => {
          if (reason !== 'select-option') {
            this.setState({ open: false })
          }
        }}
        onInputChange={(_, val, reason) => {
          this.setState({ inputValue: val })
          if (reason === 'input' && val.length > 1) {
            this.setState({ term: val, open: true })
            this.getPlaces(val)
          }
        }}
        freeSolo
        getOptionLabel={(option) => option.DisplayName || ''}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search for High Schools, Towns, or Zip Codes"
            variant="outlined"
            {...this.props.TextField}
            InputProps={{
              ...params.InputProps,
              ...this.props.TextField?.InputProps,
            }}
          />
        )}
        renderOption={(item) => this.getOption(item)}
        closeIcon={null}
      />
    )
  }
}

export default PlaceTypeahead
